import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/site/Logo";
import Overview from "../components/admin/Overview";
import Bookings from "../components/admin/Bookings";
import SellRequests from "../components/admin/SellRequests";
import ServicesAdmin from "../components/admin/ServicesAdmin";
import ModelsAdmin from "../components/admin/ModelsAdmin";
import ReviewsAdmin from "../components/admin/ReviewsAdmin";
import ContactAdmin from "../components/admin/ContactAdmin";
import ProductsAdmin from "../components/admin/ProductsAdmin";
import PurchaseRequests from "../components/admin/PurchaseRequests";
import {
  LayoutDashboard, CalendarClock, HandCoins, Wrench, Smartphone,
  Star, Phone, LogOut, ExternalLink, Menu, X, ShoppingBag, Package,
} from "lucide-react";

const NAV = [
  { key: "overview", label: "Oversikt", icon: LayoutDashboard, C: Overview },
  { key: "bookings", label: "Bestillinger", icon: CalendarClock, C: Bookings },
  { key: "sell", label: "Kunder som vil selge", icon: HandCoins, C: SellRequests },
  { key: "products", label: "Produkter", icon: Package, C: ProductsAdmin },
  { key: "purchases", label: "Kjøpsforespørsler", icon: ShoppingBag, C: PurchaseRequests },
  { key: "services", label: "Tjenester", icon: Wrench, C: ServicesAdmin },
  { key: "models", label: "Modeller og priser", icon: Smartphone, C: ModelsAdmin },
  { key: "reviews", label: "Anmeldelser", icon: Star, C: ReviewsAdmin },
  { key: "contact", label: "Kontaktinfo", icon: Phone, C: ContactAdmin },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [open, setOpen] = useState(false);

  const Active = NAV.find((n) => n.key === active)?.C || Overview;

  const doLogout = async () => { await logout(); navigate("/admin/login", { replace: true }); };

  const NavList = () => (
    <nav className="space-y-1">
      {NAV.map((n) => (
        <button
          key={n.key}
          onClick={() => { setActive(n.key); setOpen(false); }}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${active === n.key ? "bg-electric text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}
          data-testid={`admin-nav-${n.key}`}
        >
          <n.icon className="h-4.5 w-4.5" /> {n.label}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-offwhite">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-navy px-4 h-14">
        <Logo />
        <button onClick={() => setOpen((v) => !v)} className="text-white p-2" data-testid="admin-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:sticky lg:top-0 lg:h-screen lg:self-start inset-y-0 left-0 z-40 w-64 bg-navy flex-col justify-between p-4 transition-transform lg:flex lg:translate-x-0 ${open ? "flex translate-x-0" : "hidden -translate-x-full"}`}>
          <div>
            <div className="hidden lg:block px-2 py-3"><Logo /></div>
            <div className="mt-4"><NavList /></div>
          </div>
          <div className="border-t border-white/10 pt-4">
            <div className="flex items-center gap-3 px-2 pb-3">
              {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />}
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white"><ExternalLink className="h-4 w-4" /> Se nettstedet</a>
            <button onClick={doLogout} className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:bg-white/10 hover:text-white" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Logg ut</button>
          </div>
        </aside>

        {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

        {/* Content */}
        <main className="flex-1 min-w-0 p-4 sm:p-8">
          <Active />
        </main>
      </div>
    </div>
  );
}
